import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, QrCode, Ticket, CurrencyDollar, UserPlus, CheckCircle } from "@phosphor-icons/react";

const money = (c) => `$${(c / 100).toFixed(2)}`;

// Phone-first one-screen till: big buttons for items, custom QR charge, registration and pack check-ins.
export default function FrontDeskModePage() {
  const { user } = useAuth();
  const origin = window.location.origin;
  const [items, setItems] = useState([]);
  const [packs, setPacks] = useState([]);
  const [view, setView] = useState("home"); // home | packs | charge
  const [qr, setQr] = useState(null); // {title, sub, url, chargeId?}
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(null);
  const [paid, setPaid] = useState(false);
  const [cart, setCart] = useState({}); // itemId -> qty
  const [reviewUrl, setReviewUrl] = useState("");
  const pollRef = useRef(null);

  const load = () => {
    api.get("/admin/front-desk/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/front-desk/packs").then((r) => setPacks(r.data)).catch(() => {});
    api.get("/site/review-info").then((r) => setReviewUrl(r.data.review_url || "")).catch(() => {});
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const isAdmin = user && ["super_admin"].includes(user.role);
  if (!user) return <div className="min-h-screen bg-[#0A0D14] text-white grid place-items-center p-6"><Link to="/login" className="underline text-[#CCFF00]">Log in as admin to open Front Desk mode</Link></div>;
  if (!isAdmin) return <div className="min-h-screen bg-[#0A0D14] text-white grid place-items-center p-6">Front Desk mode is for admins.</div>;

  const addToCart = (it) => setCart((c) => ({ ...c, [it.id]: Math.min(20, (c[it.id] || 0) + 1) }));
  const decCart = (id) => setCart((c) => { const n = { ...c }; if ((n[id] || 0) <= 1) delete n[id]; else n[id] -= 1; return n; });
  const cartLines = items.filter((it) => cart[it.id]).map((it) => ({ ...it, qty: cart[it.id] }));
  const cartTotal = cartLines.reduce((s, l) => s + l.price_cents * l.qty, 0);

  const showCartQr = () => {
    const enc = cartLines.map((l) => `${l.id}.${l.qty}`).join("~");
    setQr({
      kind: "cart",
      title: cartLines.map((l) => `${l.qty}× ${l.name}`).join(", ").slice(0, 90),
      sub: money(cartTotal),
      url: `${origin}/buy/cart?i=${encodeURIComponent(enc)}`,
      hint: "They scan once and pay for everything together — registration & payment happen on their phone",
    });
  };

  const createCharge = async () => {
    const cents = Math.round(parseFloat(amount || 0) * 100);
    if (!cents || cents < 100) { toast.error("Enter an amount of $1 or more"); return; }
    setBusy("charge");
    try {
      const { data } = await api.post("/admin/charge", { amount_cents: cents, description: desc });
      setPaid(false);
      setQr({ title: desc || "Custom charge", sub: money(cents), url: data.url, chargeId: data.charge_id, hint: "They scan and pay on their phone — this screen flips green when paid" });
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const st = await api.get(`/admin/charge/${data.charge_id}/status`);
          if (st.data.status === "paid") { setPaid(true); clearInterval(pollRef.current); }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const consume = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/admin/front-desk/packs/${p.id}/use`);
      toast.success(`Session used — ${data.sessions_remaining} left`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const closeQr = () => { if (qr?.kind === "cart") setCart({}); setQr(null); setPaid(false); clearInterval(pollRef.current); };

  if (qr) return (
    <div className={"min-h-screen flex flex-col items-center justify-center p-6 text-center transition-colors " + (paid ? "bg-[#14532D]" : "bg-[#0A0D14]")} data-testid="till-qr-screen">
      {paid ? (
        <>
          <CheckCircle size={110} weight="fill" color="#CCFF00" />
          <h1 className="text-4xl font-extrabold font-display till-accent uppercase tracking-wide mt-3">Paid!</h1>
          <p className="till-text mt-1">{qr.title} — {qr.sub}</p>
          <p className="till-dim text-sm mt-2">Send their receipt from the Transactions list any time.</p>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-extrabold font-display till-text m-0">{qr.title}</h1>
          <div className="till-accent text-3xl font-extrabold font-display mt-1 mb-5">{qr.sub}</div>
          <div className="bg-white p-4 rounded-3xl"><QRCodeSVG value={qr.url} size={Math.min(300, window.innerWidth - 96)} level="M" /></div>
          <p className="till-dim text-sm mt-4 max-w-xs">{qr.hint}</p>
        </>
      )}
      <button onClick={closeQr} data-testid="till-qr-done"
        className="mt-8 px-10 py-4 rounded-full font-display font-bold uppercase tracking-wide border-0 cursor-pointer text-lg"
        style={{ background: "#FFE01A", color: "#000000" }}>Done</button>
    </div>
  );

  return (
    <div className={"min-h-screen bg-[#0A0D14] text-white p-4 " + (view === "home" && cartLines.length > 0 ? "pb-64" : "pb-10")} data-testid="front-desk-mode">
      <div className="flex items-center justify-between mb-4">
        <Link to="/admin" className="flex items-center gap-1.5 till-dim text-sm no-underline" data-testid="till-back"><ArrowLeft size={16} /> Operations</Link>
        <div className="font-display font-extrabold uppercase tracking-[0.2em] till-accent">Front Desk</div>
        <div className="w-16" />
      </div>

      {view === "home" && (
        <div className="grid grid-cols-2 gap-3" data-testid="till-home">
          {items.map((it) => (
            <button key={it.id} onClick={() => addToCart(it)} data-testid={`till-item-${it.id}`}
              className="relative rounded-2xl p-4 text-left border-0 cursor-pointer bg-[#181C26] active:bg-[#242a38] min-h-[110px] flex flex-col justify-between">
              {cart[it.id] > 0 && (
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full grid place-items-center font-display font-extrabold text-sm"
                  style={{ background: "#CCFF00", color: "#000000" }} data-testid={`till-item-qty-${it.id}`}>{cart[it.id]}</span>
              )}
              <div className="font-display font-bold till-text text-base leading-tight pr-7">{it.name}</div>
              <div>
                <div className="till-accent font-display font-extrabold text-xl">{money(it.price_cents)}</div>
                {it.sessions > 0 && <div className="till-dim text-xs">{it.sessions}-session pack</div>}
                {it.grants_course_id && <div className="till-dim text-xs">includes online course</div>}
              </div>
            </button>
          ))}
          <button onClick={() => setView("charge")} data-testid="till-custom-charge"
            className="rounded-2xl p-4 text-left border-2 border-dashed border-[#3A4152] bg-transparent cursor-pointer min-h-[110px] text-white">
            <CurrencyDollar size={26} color="#FFE01A" /><div className="font-display font-bold mt-2 till-text">Custom amount</div>
            <div className="till-dim text-xs">any price, QR + live paid check</div>
          </button>
          <button onClick={() => setQr({ title: "Registration", sub: "Sign the waiver once — never again", url: `${origin}/register`, hint: "New person? They scan, sign on their phone, done." })}
            data-testid="till-register"
            className="rounded-2xl p-4 text-left border-0 bg-[#1E2A1E] cursor-pointer min-h-[110px] text-white">
            <UserPlus size={26} color="#CCFF00" /><div className="font-display font-bold mt-2 till-text">Registration</div>
            <div className="till-dim text-xs">walk-in waiver QR</div>
          </button>
          {reviewUrl ? (
            <button onClick={() => setQr({ title: "Leave us a review!", sub: "★★★★★ · 30 seconds — thank you!", url: reviewUrl, hint: "Scan to open our Google review box — even two words count." })}
              data-testid="till-review"
              className="rounded-2xl p-4 text-left border-0 bg-[#2A2410] cursor-pointer min-h-[110px]">
              <div className="text-xl" style={{ color: "rgba(217,158,2,0.98)" }}>★★★★★</div>
              <div className="font-display font-bold mt-1 till-text">Review us</div>
              <div className="till-dim text-xs">hand them your phone</div>
            </button>
          ) : (
            <button onClick={() => { window.location.href = "/admin?tab=frontdesk"; }} data-testid="till-review-setup"
              className="rounded-2xl p-4 text-left border-2 border-dashed border-[#5A4A10] bg-transparent cursor-pointer min-h-[110px] text-white">
              <div className="text-xl" style={{ color: "rgba(217,158,2,0.55)" }}>★★★★★</div>
              <div className="font-display font-bold mt-1 till-text">Review us — not set up</div>
              <div className="till-dim text-xs">tap here, then paste your Google review link in the "Walk-in registration" card and Save</div>
            </button>
          )}
          <button onClick={() => setView("packs")} data-testid="till-packs"
            className={"rounded-2xl p-4 text-left border-0 bg-[#26202E] cursor-pointer min-h-[110px] text-white " + (reviewUrl ? "" : "col-span-2")}>
            <div className="flex items-center gap-2"><Ticket size={24} color="#F87171" /><span className="font-display font-bold till-text">Pack check-in</span></div>
            <div className="till-dim text-xs mt-1">{packs.filter((p) => p.sessions_remaining > 0).length} active pack(s) — tap to use a session</div>
          </button>
        </div>
      )}

      {view === "home" && cartLines.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#12151E] border-t-2 border-[#CCFF00] p-4 pb-6" data-testid="till-cart-bar">
          <div className="max-w-md mx-auto">
            {cartLines.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-2 py-1" data-testid={`till-cart-line-${l.id}`}>
                <span className="till-text text-sm truncate">{l.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => decCart(l.id)} data-testid={`till-cart-minus-${l.id}`}
                    className="w-9 h-9 rounded-full border-0 cursor-pointer font-bold text-lg bg-[#2A3040] text-white">−</button>
                  <span className="till-accent font-display font-extrabold w-5 text-center" data-testid={`till-cart-qty-${l.id}`}>{l.qty}</span>
                  <button onClick={() => addToCart(l)} data-testid={`till-cart-plus-${l.id}`}
                    className="w-9 h-9 rounded-full border-0 cursor-pointer font-bold text-lg bg-[#2A3040] text-white">+</button>
                  <span className="till-text text-sm font-display font-bold w-16 text-right">{money(l.price_cents * l.qty)}</span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#2A3040]">
              <button onClick={() => setCart({})} data-testid="till-cart-clear"
                className="bg-transparent border-0 cursor-pointer text-white/50 text-sm underline underline-offset-2">Clear</button>
              <button onClick={showCartQr} data-testid="till-cart-charge"
                className="px-8 py-3.5 rounded-full font-display font-bold uppercase tracking-wide border-0 cursor-pointer text-base"
                style={{ background: "#FFE01A", color: "#000000" }}>Charge {money(cartTotal)} →</button>
            </div>
          </div>
        </div>
      )}

      {view === "charge" && (
        <div data-testid="till-charge-form">
          <button onClick={() => setView("home")} className="text-white/60 text-sm bg-transparent border-0 mb-3 cursor-pointer">← back</button>
          <label className="block text-xs text-white/60 mb-1">Amount (CAD)</label>
          <input className="w-full rounded-xl px-4 py-4 mb-3 border-0 bg-white text-[#000000] text-2xl font-display font-bold" type="number" min="1" step="0.01"
            inputMode="decimal" placeholder="100" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="till-amount" />
          <label className="block text-xs text-white/60 mb-1">Description (shows on their receipt)</label>
          <input className="w-full rounded-xl px-4 py-3 mb-4 border-0 bg-white text-[#000000]" placeholder="Drop-in session"
            value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="till-desc" />
          <div className="flex gap-2 flex-wrap mb-4">
            {items.map((it) => (
              <button key={it.id} className="px-3 py-1.5 rounded-full text-xs bg-[#181C26] text-white/80 border border-[#3A4152] cursor-pointer"
                onClick={() => { setAmount((it.price_cents / 100).toFixed(2)); setDesc(it.name); }}>{it.name}</button>
            ))}
          </div>
          <button onClick={createCharge} disabled={busy === "charge"} data-testid="till-create-charge"
            className="w-full py-4 rounded-full font-display font-bold uppercase tracking-wide text-lg border-0 cursor-pointer disabled:opacity-60"
            style={{ background: "#FFE01A", color: "#000000" }}>
            {busy === "charge" ? "Creating…" : "Show payment QR"}
          </button>
        </div>
      )}

      {view === "packs" && (
        <div data-testid="till-packs-list">
          <button onClick={() => setView("home")} className="text-white/60 text-sm bg-transparent border-0 mb-3 cursor-pointer">← back</button>
          {packs.length === 0 && <p className="text-white/50">No packs sold yet.</p>}
          <div className="flex flex-col gap-2">
            {packs.map((p) => (
              <div key={p.id} className="rounded-2xl bg-[#181C26] p-4 flex items-center justify-between gap-3" data-testid={`till-pack-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-display font-bold till-text truncate">{p.customer_name || p.customer_email || p.customer_phone || "Customer"}</div>
                  <div className="till-dim text-xs truncate">{p.item_name}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={"font-display font-extrabold text-lg " + (p.sessions_remaining > 0 ? "till-accent" : "text-[#F87171]")}>{p.sessions_remaining}/{p.sessions_total}</span>
                  <button onClick={() => consume(p)} disabled={busy === p.id || p.sessions_remaining <= 0} data-testid={`till-pack-use-${p.id}`}
                    className="px-5 py-3 rounded-full font-display font-bold border-0 cursor-pointer disabled:opacity-40"
                    style={{ background: "#FFE01A", color: "#000000" }}>Use 1</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
